/**
 * Contexto de Autenticación
 *
 * Maneja el estado de autenticación del usuario en toda la aplicación
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import awsConfig from '../aws-config';
import { createOrganization as createOrganizationAPI } from '../services/api';
import { storage } from '../utils/storage';
import { getBasename } from '../utils/basename';

// Configurar Amplify
Amplify.configure(awsConfig);

// Almacenar tokens en cookies con atributos Secure y SameSite en lugar de localStorage.
// Nota: no son httpOnly (Amplify necesita leer los tokens desde JS), pero eliminan
// el vector localStorage y agregan protección CSRF via SameSite.
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: window.location.hostname,
    secure: window.location.protocol === 'https:',
    // Cookie path debe coincidir con el base path servido, si no el browser no
    // manda el token bajo /citame/admin (o /consultame/admin) y se pierde la
    // sesión post-login. Vertical-aware vía getBasename (NER-4111): scopea la
    // cookie a la vertical activa en vez de caer a '/' bajo Consultame.
    path: getBasename() ? `${getBasename()}/` : '/',
    sameSite: 'strict',
    expires: 30,
  })
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Traduce el detalle específico que Cognito devuelve en err.message (en inglés) a un
// texto UX-friendly en español. Devuelve null si no reconoce el patrón, para que el
// caller decida el fallback. Ver NER-4183: InvalidParameterException/InvalidPasswordException
// traían la causa real (política de password, formato de teléfono/email) que el mapeo
// genérico pisaba, dejando al usuario sin saber qué campo corregir.
const translateCognitoDetail = (message) => {
  if (!message || typeof message !== 'string') return null;
  const msg = message.toLowerCase();

  // Política de contraseña (Cognito: "Password did not conform with policy: ...")
  if (
    msg.includes('password did not conform') ||
    msg.includes('password must') ||
    msg.includes('password not long')
  ) {
    if (msg.includes('symbol')) return 'La contraseña debe tener al menos un símbolo (ej: !@#$%).';
    if (msg.includes('uppercase')) return 'La contraseña debe tener al menos una letra mayúscula.';
    if (msg.includes('lowercase')) return 'La contraseña debe tener al menos una letra minúscula.';
    if (msg.includes('numeric') || msg.includes('number'))
      return 'La contraseña debe tener al menos un número.';
    if (msg.includes('long enough') || msg.includes('length'))
      return 'La contraseña debe tener al menos 8 caracteres.';
    return 'La contraseña no cumple los requisitos: mínimo 8 caracteres, con mayúscula, minúscula, número y símbolo.';
  }

  // Formato de teléfono (E.164)
  if (msg.includes('phone')) {
    return 'Formato de teléfono inválido. Usá el formato internacional +54XXXXXXXXXX (E.164).';
  }

  // Formato de email
  if (msg.includes('email')) {
    return 'Formato de email inválido. Verificá que esté bien escrito (ej: nombre@dominio.com).';
  }

  // Cognito a veces reporta un email malformado como "username"
  if (msg.includes('username')) {
    return 'El email ingresado no es válido. Verificá que esté bien escrito.';
  }

  return null;
};

// Mapeo de errores de Cognito a mensajes en español
const translateCognitoError = (err) => {
  const errorMap = {
    NotAuthorizedException: 'Usuario o contraseña incorrectos',
    UserNotFoundException: 'No existe ninguna cuenta con ese email',
    UserNotConfirmedException:
      'Tu cuenta no ha sido confirmada. Revisa tu email para el código de verificación.',
    UsernameExistsException: 'Ya existe una cuenta con ese email',
    InvalidPasswordException: 'La contraseña no cumple los requisitos de seguridad',
    CodeMismatchException: 'El código de verificación es incorrecto',
    ExpiredCodeException: 'El código de verificación ha expirado. Solicita uno nuevo.',
    LimitExceededException: 'Demasiados intentos. Por favor espera antes de intentar nuevamente.',
    TooManyRequestsException: 'Demasiadas solicitudes. Por favor espera un momento.',
    TooManyFailedAttemptsException: 'Demasiados intentos fallidos. Por favor espera.',
    InvalidParameterException: 'Parámetros inválidos. Verifica los datos ingresados.',
    PasswordResetRequiredException: 'Debes restablecer tu contraseña. Revisa tu email.',
    UserLambdaValidationException: 'Error al validar el usuario.',
    AliasExistsException: 'Ya existe una cuenta con ese email.',
  };

  // Errores donde el detalle específico de Cognito importa (política de password, formato
  // de teléfono/email). Priorizar ese detalle traducido sobre el mensaje genérico, que
  // ocultaba la causa real y no le decía al usuario qué corregir. Ver NER-4183.
  const DETAIL_SENSITIVE = new Set([
    'InvalidParameterException',
    'InvalidPasswordException',
    'UserLambdaValidationException',
  ]);

  if (DETAIL_SENSITIVE.has(err.name)) {
    const detail = translateCognitoDetail(err.message);
    if (detail) return detail;
    // Patrón no reconocido: preferimos el mensaje crudo de Cognito antes que el genérico
    // que oculta la causa (aunque venga en inglés, es más útil para corregir el campo).
    if (err.message) return err.message;
  }

  return errorMap[err.name] || err.message;
};

// Keys para localStorage
const PENDING_ORGANIZATION_KEY = 'pendingOrganizationData';
const ORGANIZATION_ID_KEY = 'organizationId';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPasswordChallenge, setPendingPasswordChallenge] = useState(null);

  // Verificar si hay un usuario autenticado al cargar la app
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh });

      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        attributes: currentUser.signInDetails?.loginId,
        tokens: {
          idToken: session.tokens?.idToken?.toString(),
          accessToken: session.tokens?.accessToken?.toString(),
          refreshToken: session.tokens?.refreshToken?.toString(),
        },
      });

      // Citame: la identidad y el tenant (businessId) salen del claim Cognito
      // `custom:businessId` del idToken (User Pool propio de Citame, NER-1457) y
      // los resuelve el authorizer del backend en cada request. El admin NO
      // consulta el modelo Resto de organización/restaurante (`/organizations/me`,
      // `/organizations/{id}/my-restaurants`): esos endpoints NO existen en el
      // backend Citame y su 403 (ruta inexistente en API Gateway) se reportaba en
      // consola como un (falso) error de CORS, rompiendo el boot (NER-1774).
      // Se deja `organization` en null: la cadena Resto de suscripción/onboarding
      // queda dormida (guardada por `organization`) hasta su remoción completa
      // en la migración (NER-1563).
      setOrganization(null);
      storage.remove(ORGANIZATION_ID_KEY);
    } catch (err) {
      // No authenticated user — expected on first load
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signIn({ username: email, password });

      if (result.isSignedIn) {
        await checkUser();
        return { success: true };
      } else if (result.nextStep) {
        const { signInStep } = result.nextStep;

        // Caso especial: Usuario necesita cambiar contraseña
        if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setPendingPasswordChallenge({ username: email });
          return {
            success: false,
            requiresNewPassword: true,
            message: 'Debes cambiar tu contraseña temporal',
          };
        }

        return {
          success: false,
          nextStep: signInStep,
          message: 'Acción adicional requerida',
        };
      }
    } catch (err) {
      console.error('Error en login:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return {
        success: false,
        error: translatedError,
        requiresConfirmation: err.name === 'UserNotConfirmedException',
        email: email,
      };
    }
  };

  const confirmNewPassword = async (newPassword, name) => {
    try {
      setError(null);

      if (!pendingPasswordChallenge) {
        throw new Error('No hay un desafío de contraseña pendiente');
      }

      // Confirming new password challenge

      // Confirmar el cambio de contraseña con Amplify
      // Incluir atributos requeridos por Cognito
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            name: name,
          },
        },
      });

      if (result.isSignedIn) {
        // Password changed successfully
        setPendingPasswordChallenge(null);
        await checkUser();
        return { success: true };
      } else {
        console.warn('[AuthContext] Contraseña cambiada pero requiere más pasos:', result.nextStep);
        return {
          success: false,
          nextStep: result.nextStep?.signInStep,
          message: 'Se requieren pasos adicionales',
        };
      }
    } catch (err) {
      console.error('Error confirmando nueva contraseña:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message,
      };
    }
  };

  const register = async (email, password, name, userAttributes = {}) => {
    try {
      setError(null);

      // Preparar atributos de Cognito (solo los soportados por defecto)
      const cognitoAttributes = {
        email,
        name,
      };

      // Si hay phone number, agregarlo
      if (userAttributes.phone) {
        cognitoAttributes.phone_number = userAttributes.phone;
      }

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: cognitoAttributes,
        },
      });

      // Guardar todos los datos adicionales en localStorage para usarlos después de confirmar
      // Esto incluye organizationName, CUIT, dirección, etc.
      const pendingData = {
        email: email,
        name: name,
        organizationName: userAttributes.organizationName || name,
        phone: userAttributes.phone || '',
        cuit: userAttributes.cuit || '',
        address: userAttributes.address || '',
        city: userAttributes.city || '',
        province: userAttributes.province || '',
        country: userAttributes.country || '',
        postalCode: userAttributes.postalCode || '',
        accountType: userAttributes.accountType || 'person',
        companyName: userAttributes.companyName || '',
        restaurantName: userAttributes.restaurantName || '',
        restaurantSlug: userAttributes.restaurantSlug || '',
      };

      storage.set(PENDING_ORGANIZATION_KEY, JSON.stringify(pendingData));

      return {
        success: true,
        userId: result.userId,
        nextStep: result.nextStep.signUpStep,
      };
    } catch (err) {
      console.error('Error en registro:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return {
        success: false,
        error: translatedError,
      };
    }
  };

  const confirmRegistration = async (email, code) => {
    try {
      setError(null);
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });

      return { success: true };
    } catch (err) {
      console.error('Error confirmando registro:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return {
        success: false,
        error: translatedError,
      };
    }
  };

  const createOrganizationAfterSignup = async () => {
    try {
      // Recuperar datos pendientes de localStorage
      const pendingDataString = storage.get(PENDING_ORGANIZATION_KEY);
      if (!pendingDataString) {
        return { success: false, error: 'No hay datos de organización pendientes' };
      }

      const pendingData = JSON.parse(pendingDataString);
      const result = await createOrganizationAPI(pendingData);

      // Actualizar el estado local
      if (result.organization) {
        setOrganization(result.organization);

        // Guardar organizationId en localStorage
        if (result.organization.organizationId) {
          storage.set(ORGANIZATION_ID_KEY, result.organization.organizationId);
        }
      }

      // Limpiar datos pendientes
      storage.remove(PENDING_ORGANIZATION_KEY);

      return { success: true, organization: result.organization };
    } catch (err) {
      console.error('Error creando organización:', err);
      return {
        success: false,
        error: err.response?.data?.message || err.message,
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      await resetPassword({ username: email });
      return { success: true };
    } catch (err) {
      console.error('Error en forgot password:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return { success: false, error: translatedError };
    }
  };

  const confirmForgotPassword = async (email, code, newPassword) => {
    try {
      setError(null);
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return { success: true };
    } catch (err) {
      console.error('Error confirmando reset password:', err);
      const translatedError = translateCognitoError(err);
      setError(translatedError);
      return { success: false, error: translatedError };
    }
  };

  const clearPendingOrganization = () => {
    storage.remove(PENDING_ORGANIZATION_KEY);
  };

  const hasPendingOrganization = () => {
    return storage.get(PENDING_ORGANIZATION_KEY) !== null;
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setOrganization(null);
      // Limpiar datos persistidos (org + restaurant + auth cache)
      storage.remove(ORGANIZATION_ID_KEY);
      storage.remove(PENDING_ORGANIZATION_KEY);
      storage.remove('selectedOrganizationId');
      storage.remove('selectedOrganizationName');
      storage.remove('userRole');
      storage.remove('allRestaurants');
      storage.remove('selectedRestaurantId');
      storage.remove('selectedRestaurantName');
      return { success: true };
    } catch (err) {
      console.error('Error en logout:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message,
      };
    }
  };

  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (err) {
      console.error('Error obteniendo token:', err);
      return null;
    }
  };

  const value = {
    user,
    organization,
    loading,
    error,
    login,
    logout,
    register,
    confirmRegistration,
    confirmNewPassword,
    forgotPassword,
    confirmForgotPassword,
    checkUser,
    getAccessToken,
    createOrganizationAfterSignup,
    clearPendingOrganization,
    hasPendingOrganization,
    pendingPasswordChallenge,
    authLoading: loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
